/**
 * Lightweight PII scrubber for error report payloads.
 *
 * Applied to message, stackTrace, and console/API logs before storage and email.
 * Catches high-risk Australian clinical PII patterns (Medicare, NDIS, phone, email, DOB).
 * Names are not detectable via regex — the full pii-stripper.ts is not applicable here
 * as it requires structured clinical context. This is a best-effort defence layer.
 */

export function scrubPii(text: string | null | undefined): string | null {
  if (!text) return text ?? null;
  return text
    // Order matters: the specific-prefix rules (04 mobile, 43 NDIS) run BEFORE the
    // generic 10-digit Medicare rule. Otherwise a 10-digit mobile (04XXXXXXXX) would
    // be caught by \d{10} and mislabelled [MEDICARE-REDACTED] — still redacted (no
    // leak) but the wrong label, and the label drives triage.
    .replace(/\b04\d{8}\b/g, '[PHONE-REDACTED]')          // Australian mobile (04XXXXXXXX)
    // No leading \b: a \b before "+" only matches when a word char immediately
    // precedes the "+", so "call +61412345678" (space before +) never matched and
    // the number leaked through. "+61" is a distinctive enough anchor on its own.
    .replace(/\+61\s?4\d{8}\b/g, '[PHONE-REDACTED]')     // AU intl format (+614XXXXXXXX)
    // Spaced/grouped mobiles (+61 412 345 678) and +61 landlines (+61 2 9876 5432)
    // slip past the compact rule above — its \s? allows only one space and it
    // hardcodes the mobile leading 4. Match +61 then 9 digits with optional internal
    // spaces/dashes. Over-redaction is safe here: a mangled number beats a leak (BL-002).
    .replace(/\+61[\s-]?(?:\d[\s-]?){8}\d/g, '[PHONE-REDACTED]') // AU intl, spaced/landline
    // BL-003: separator-tolerant coverage. The rules above only catch compact 04
    // mobiles and clean +61 forms; real AU error text carries domestic landlines,
    // dashed mobiles, and dot/paren/0061-prefixed numbers far more often. Both
    // rules use bounded {8}/{9} repetitions over a separator class disjoint from
    // \d, so there is no catastrophic backtracking. Over-redaction is the safe
    // direction (a mangled number beats a leaked one). Run before NDIS/Medicare so
    // a domestic landline gets the PHONE label, not a mislabelled [MEDICARE-REDACTED].
    .replace(/(?:\+?61|0061)[\s.()\-]{0,3}(?:0[\s.()\-]{0,2})?(?:\d[\s.()\-]{0,2}){8}\d/g, '[PHONE-REDACTED]') // AU intl alt: 0061 / bare 61 / (0) trunk / dot separators
    .replace(/\b0[2-478](?:[\s.()\-]{0,2}\d){8}\b/g, '[PHONE-REDACTED]') // AU domestic: 0[2-478] landline + spaced/dashed mobile
    .replace(/\b43\d{7}\b/g, '[NDIS-REDACTED]')          // NDIS (9-digit, starts with 43)
    .replace(/\b\d{10}\b/g, '[MEDICARE-REDACTED]')       // Medicare numbers (10 digits)
    // Local/domain length-capped (RFC-realistic 64/255) so a long non-email run
    // can't trigger O(n²) backtracking. Real emails are well under these caps —
    // redaction outcome unchanged. Mirrors the client bugsnag-client.js bound.
    .replace(/[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]{1,255}\.[a-zA-Z]{2,}/g, '[EMAIL-REDACTED]')
    .replace(/\b\d{2}\/\d{2}\/\d{4}\b/g, '[DOB-REDACTED]')   // DD/MM/YYYY display dates
    // ISO YYYY-MM-DD — SEER stores DOBs in this format (e.g. 2015-03-15), so the
    // DD/MM/YYYY rule above missed virtually every real date of birth. The trailing
    // \b keeps this scoped to standalone date-shaped values (real DOBs); an ISO
    // datetime like 2026-06-08T12:00:00Z is left intact (digit-then-"T" = no boundary).
    .replace(/\b\d{4}-\d{2}-\d{2}\b/g, '[DOB-REDACTED]');
}

export function scrubContext(
  context:
    | {
        consoleLogs?: { level: string; message: string; timestamp: string }[];
        apiCalls?: {
          method: string;
          url: string;
          status: number;
          duration: number;
          responsePreview?: string | null;
        }[];
        viewport?: { width: number; height: number };
      }
    | null
    | undefined,
): typeof context {
  if (!context) return context;
  return {
    ...context,
    consoleLogs: context.consoleLogs?.map((log) => ({
      ...log,
      message: scrubPii(log.message) ?? log.message,
    })),
    apiCalls: context.apiCalls?.map((call) => ({
      ...call,
      responsePreview: scrubPii(call.responsePreview ?? null) ?? undefined,
    })),
  };
}
